'use client'

import { ButtonIconTransparent } from '@fukku/button/Icon/Transparent/ButtonIconTransparent'
import textBodyM from '@fukku/styles/texts/textBodyM.module.css'
import textHeadingM from '@fukku/styles/texts/textHeadingM.module.css'
import { cx } from 'classix'
import { IconMinus } from 'icons/components/IconMinus'
import { IconPrevLarge } from 'icons/components/IconPrevLarge'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import type { ReactNode } from 'react'
import { useResponsive } from 'responsive/hooks/useResponsive/useResponsive'

import { DialogType, useDialogContext } from '../hooks/useDialogContext'
import { CloseButton } from './CloseButton/CloseButton'
import styles from './Header.module.css'

export type HeaderProps = {
	children?: ReactNode
	langTitle?: string
	title?: string
	subtitle?: string
	onBack?: () => void
	onMinus?: () => void
	onClose?: () => void
	className?: string
	'data-testid'?: string
	alternativeAction?: ReactNode
	titleDataTestId?: string
	subtitleDataTestId?: string
}

export function Header({
	title,
	subtitle,
	children,
	'data-testid': dataTestId,
	titleDataTestId,
	className = '',
	onBack = undefined,
	alternativeAction,
	onMinus = undefined,
	onClose = undefined,
	langTitle,
	subtitleDataTestId,
}: Readonly<HeaderProps>) {
	const { type } = useDialogContext()
	const onBottomSheet = type === DialogType.BottomSheet
	const { t } = useLabels()
	const { isTouchable } = useResponsive()
	const hasDraggableControl = onBottomSheet && isTouchable
	const titleComponent = title ?? children

	return (
		<div
			data-dialog-header
			data-testid={dataTestId}
			className={cx(
				styles.header,
				hasDraggableControl && styles.barPadding,
				!titleComponent && styles.empty,
				!!subtitle && styles.hasSubtitle,
				className
			)}
		>
			{onBack && (
				<div className={styles.back}>
					<ButtonIconTransparent className={styles.area} onClick={onBack}>
						<IconPrevLarge
							width={20}
							height={20}
							rotateRtl={true}
							alt={t('accessibility.icon.back.text')}
						/>
					</ButtonIconTransparent>
				</div>
			)}

			<div className={styles.fill} lang={langTitle}>
				{titleComponent && (
					<>
						<h1
							className={cx(styles.title, textHeadingM.className)}
							data-testid={titleDataTestId}
						>
							{titleComponent}
						</h1>
						{subtitle && (
							<h2
								className={cx(styles.subtitle, textBodyM.className)}
								data-testid={subtitleDataTestId}
							>
								{subtitle}
							</h2>
						)}
					</>
				)}
			</div>

			{alternativeAction && (
				<div className={cx(styles.alternative)}>{alternativeAction}</div>
			)}

			{!hasDraggableControl && onMinus && (
				<div className={styles.minus}>
					<ButtonIconTransparent className={styles.area} onClick={onMinus}>
						<IconMinus
							rotateRtl={true}
							alt={t('accessibility.icon.minus.text')}
						/>
					</ButtonIconTransparent>
				</div>
			)}
			<CloseButton
				onClose={onClose}
				hasDraggableControl={hasDraggableControl}
			/>
		</div>
	)
}
